/**
 * 数据转换与配置工具函数
 * 用于处理业务数据的格式转换和配置生成
 */

/**
 * 配置著作权人数据（转换为后端需要的格式）
 * @param {Array} applicants - 著作权人数组
 * @returns {Array} 转换后的著作权人数组
 * @example
 * configApplicants([{
 *   name: '张三',
 *   country: 'CN',
 *   areaCode: ['110000', '110100', '110101'],
 *   ...
 * }])
 */
export const configApplicants = (applicants) => {
  if (!applicants || applicants.length === 0) {
    return [];
  }

  return applicants.map(item => ({
    country: item.country,
    province: item.areaCode.length ? item.areaCode[0] : '',
    city: item.areaCode.length ? item.areaCode[1] : '',
    area: item.areaCode.length > 2 ? item.areaCode[2] : '', // 添加区域字段，兼容 new.vue
    peopleType: item.peopleType,
    idType: item.idType,
    idNumber: item.idNumber,
    name: item.name,
    loginName: item.loginName || '',
    accountType: item.accountType
  }));
};

/**
 * 配置提交参数（整合所有表单数据）
 * @param {Object} softForm - 软件表单数据
 * @param {Object} options - 额外配置项
 * @param {string} options.writeId - 写作ID
 * @param {string} options.packageId - 套餐ID
 * @param {string} options.originWriteId - 原始写作ID
 * @param {string} options.ownerId - 所有者ID
 * @param {number} options.applicantType - 申请人类型（0:个人, 1:企业）
 * @param {string} options.requirement - 其他需求
 * @param {Array} options.fileResultList - 文件结果列表
 * @returns {Object} 配置后的提交参数
 */
export const configSubmitParams = (softForm, options = {}) => {
  const {
    writeId,
    packageId,
    originWriteId,
    ownerId,
    applicantType,
    requirement,
    fileResultList
  } = options;

  // 基础参数（浅拷贝软件表单数据）
  const params = { ...softForm };

  // 添加其他需求
  params.requirement = requirement || '';

  // 添加ID相关参数
  if (writeId) {
    params.id = writeId;
  }
  if (packageId) {
    params.companyPackageId = packageId;
  }
  if (originWriteId) {
    params.originWriteId = originWriteId;
  }
  if (ownerId) {
    params.ownerId = ownerId;
  }

  // 设置著作权人类型
  if (applicantType === 1) {
    params.copyrightOwnerKind = 'SINGLE_ENTERPRISE'; // 企业
  } else {
    params.copyrightOwnerKind = 'SINGLE_PEOPLE'; // 个人
  }

  // 移除前端专用字段
  delete params.applicants;
  delete params.country;
  delete params.idType;
  delete params.peopleType;
  delete params.idNumber;
  delete params.areaCode;

  // 过滤空的著作权人
  const validApplicants = softForm.applicants.filter(
    item => item.name && item.name.trim().length
  );

  // 序列化著作权人数据
  params.applicants = JSON.stringify(configApplicants(validApplicants));

  // 生成申请人字符串（用顿号连接）
  params.applicant = validApplicants.map(item => item.name).join('、');

  // 添加文件结果列表
  if (fileResultList) {
    params.otherFile = JSON.stringify(fileResultList);
  }

  return params;
};

/**
 * 配置新版大纲JSON数据（解析并转换为前端展示格式）
 * @param {string} outlineJsonStr - 大纲JSON字符串
 * @returns {Object} 配置后的大纲对象
 */
export const configNewOutlineJson = (outlineJsonStr) => {
  const obj = JSON.parse(outlineJsonStr);
  const featStru = JSON.parse(JSON.stringify(obj.featStru));

  // 转换特征结构为统一格式
  const allFeatures = featStru.map(item => {
    if (item.children && item.children.length) {
      // 有子节点的分组
      const children = item.children.map(child => ({
        featureId: child.featureId,
        featureName: child.featureName,
        title: child.featureName
      }));

      return {
        groupId: item.groupId,
        groupName: item.groupName,
        children: children,
        title: item.groupName
      };
    } else {
      // 无子节点的单个特征
      return {
        groupId: item.groupId,
        groupName: item.groupName,
        children: [],
        featureId: item.featureId,
        title: item.groupName
      };
    }
  });

  return {
    allFeatures,
    templateName: obj.templateName,
    isGroup: obj.isGroup,
    promptId: 102,
    featStru: obj.featStru,
    templateId: obj.templateId,
    writeId: obj.writeId,
    softName: obj.softName,
    introduction: obj.introduction,
    features: obj.features
  };
};

/**
 * 设置开发方式（根据著作权人数量自动判断）
 * @param {Array} applicants - 著作权人数组
 * @returns {string} 开发方式 ('SINGLE' | 'COOPERATE')
 */
export const determineDevelopMode = (applicants) => {
  // 过滤出有效的著作权人名称
  const validNames = applicants
    .map(item => item.name?.trim())
    .filter(name => name && name.length > 0);

  // 多个著作权人为合作开发，单个为单独开发
  return validNames.length > 1 ? 'COOPERATE' : 'SINGLE';
};

/**
 * 初始化著作权人对象（创建默认结构）
 * @param {Object} data - 初始数据（可选）
 * @returns {Object} 著作权人对象
 */
export const createApplicantObject = (data = {}) => {
  return {
    country: data.country || '',
    areaCode: data.areaCode || '',
    peopleType: data.peopleType || '',
    idType: data.idType || '',
    name: data.name || '',
    idNumber: data.idNumber || '',
    displayName: data.displayName || '',
    loginName: data.loginName || '',
    accountType: data.accountType || '',
    uid: data.uid || generateUniqueId()
  };
};

/**
 * 解析后端返回的著作权人数据
 * @param {string} applicantsJsonStr - 著作权人JSON字符串
 * @param {Function} setDisplayName - 设置显示名称的函数
 * @param {Function} generateId - 生成唯一ID的函数
 * @returns {Array} 著作权人数组
 */
export const parseApplicantsFromServer = (applicantsJsonStr, setDisplayName, generateId) => {
  if (!applicantsJsonStr) {
    return [createApplicantObject({ uid: generateId() })];
  }

  try {
    const applicants = JSON.parse(applicantsJsonStr);

    if (applicants.length === 0) {
      return [createApplicantObject({ uid: generateId() })];
    }

    return applicants.map(owner => ({
      country: owner.country,
      areaCode: owner.province ? [owner.province, owner.city] : '',
      peopleType: owner.peopleType,
      idType: owner.idType,
      idNumber: owner.idNumber,
      name: owner.name,
      displayName: setDisplayName(owner.name, owner.loginName),
      loginName: owner.loginName || '',
      accountType: owner.accountType,
      uid: generateId()
    }));
  } catch (error) {
    console.error('解析著作权人数据失败:', error);
    return [createApplicantObject({ uid: generateId() })];
  }
};

/**
 * 配置自动检测数据
 * @param {Object} softForm - 软件表单
 * @param {Function} configApplicantsFn - 配置著作权人的函数
 * @param {number} step - 检测步骤
 * @returns {Object} 自动检测数据对象
 */
export const configAutoCheckData = (softForm, configApplicantsFn, step = 0) => {
  return {
    step,
    soft: { ...softForm },
    applicants: configApplicantsFn()
  };
};

/**
 * 生成唯一ID的辅助函数（为了兼容性单独导出）
 * @returns {string} 唯一ID
 */
const generateUniqueId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * 检查著作权人列表中是否包含个人类型
 * @param {Array} applicants - 著作权人数组
 * @param {Function} checkApiFunc - 调用检测API的函数（可选）
 * @returns {Promise<boolean>} 是否包含个人
 */
export const checkHasPersonalApplicant = async (applicants, checkApiFunc) => {
  // 过滤出有效的著作权人
  const validApplicants = applicants.filter(
    item => item.name && item.name.trim().length > 0
  );

  if (validApplicants.length === 0) {
    return false;
  }

  // 检查是否已明确标记为个人类型
  const hasPersonalType = validApplicants.some(
    item => item.accountType === 0 || item.accountType === '0'
  );

  if (hasPersonalType) {
    return true;
  }

  // 检查是否有未确定类型的著作权人
  const hasEmptyType = validApplicants.some(
    item => item.accountType === ''
  );

  if (!hasEmptyType) {
    return false;
  }

  // 如果有未确定类型且提供了检测API，调用API判断
  if (checkApiFunc && typeof checkApiFunc === 'function') {
    const names = validApplicants.map(item => item.name);
    const result = await checkApiFunc(names);
    return Boolean(result);
  }

  return false;
};
